import React from "react";
import { CircleMarker, Pane, Polyline } from "react-leaflet";
import { Destination, Route, RouteSection } from "../api/types";
import { styleForSection } from "../tflColours.ts";

// Identifies one section of one route, for hover-highlighting.
export interface SectionRef {
  routeIdx: number;
  sectionIdx: number;
}

// Stations with unknown coordinates sit at lat/lon (0, 0); don't let them
// drag polylines off to Null Island.
const hasCoords = (stop: Destination): boolean =>
  Math.abs(stop.location.x) > 0.01 || Math.abs(stop.location.y) > 0.01;

const positionsFor = (section: RouteSection): [number, number][] =>
  section.stops
    .filter(hasCoords)
    .map((stop) => [stop.location.x, stop.location.y]);

// Draws every section of every route as a polyline through its stops, with
// a small circle wherever the mode/line changes.
const RouteLayer = ({
  routes,
  highlight,
}: {
  routes: Route[];
  highlight: SectionRef | null;
}) => {
  const isHighlighted = (routeIdx: number, sectionIdx: number): boolean =>
    highlight !== null &&
    highlight.routeIdx === routeIdx &&
    highlight.sectionIdx === sectionIdx;

  return (
    // Above the travel-time circles pane (zIndex 500), below markers (600).
    <Pane name="routes" style={{ zIndex: 550 }}>
      {routes.map((route, routeIdx) =>
        // White casing underneath each solid line so routes don't get lost
        // against the coloured travel-time map. Dashed walking sections are
        // left uncased (a solid casing would fill their gaps).
        route.sections.map((section, sectionIdx) => {
          if (styleForSection(section).dashArray) {
            return null;
          }
          return (
            <Polyline
              key={`case-${routeIdx}-${sectionIdx}`}
              positions={positionsFor(section)}
              pathOptions={{
                color: "white",
                weight: isHighlighted(routeIdx, sectionIdx) ? 14 : 10,
                opacity:
                  highlight !== null && !isHighlighted(routeIdx, sectionIdx)
                    ? 0.25
                    : 1,
              }}
            />
          );
        }),
      )}
      {routes.map((route, routeIdx) =>
        route.sections.map((section, sectionIdx) => (
          <Polyline
            key={`line-${routeIdx}-${sectionIdx}`}
            positions={positionsFor(section)}
            pathOptions={{
              ...styleForSection(section),
              weight: isHighlighted(routeIdx, sectionIdx) ? 10 : 6,
              opacity:
                highlight !== null && !isHighlighted(routeIdx, sectionIdx)
                  ? 0.25
                  : 0.9,
            }}
          />
        )),
      )}
      {routes.map((route, routeIdx) =>
        // A dot at the start of every section after the first, i.e. wherever
        // you change mode or line.
        route.sections.slice(1).map((section, i) => {
          const stop = section.stops.filter(hasCoords)[0];
          if (!stop) {
            return null;
          }
          return (
            <CircleMarker
              key={`dot-${routeIdx}-${i}`}
              center={[stop.location.x, stop.location.y]}
              radius={5}
              pathOptions={{
                color: "#333333",
                weight: 2,
                fillColor: "white",
                fillOpacity: 1,
              }}
            />
          );
        }),
      )}
    </Pane>
  );
};

export default RouteLayer;
